import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import i18n from '../i18n';
import { normalizeLocale } from '../i18n/languages';
import { buildLocalSportTypeGroups } from '../constants/localSportTypes';

export type SportTypeItem = {
  id: number;
  name: string;
  emoji: string;
  color?: string;
  venueName?: string;
  groupNoun?: string;
  studentNoun?: string;
  coachNoun?: string;
  lessonNoun?: string;
  subscriptionNoun?: string;
  venueExample?: string;
  groupExample?: string;
  hasDemoSchool?: boolean;
  launchPriority: number;
  typicalInventory?: string[];
};

export type SportTypeGroup = {
  groupId: number;
  groupName: string;
  groupEmoji: string;
  color?: string;
  sports: SportTypeItem[];
};

const api = axios.create({ baseURL: getApiBaseUrl() });

api.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = normalizeLocale(i18n.resolvedLanguage || i18n.language || 'en');
  return config;
});

const catalogCache = new Map<string, SportTypeGroup[]>();
const catalogPromise = new Map<string, Promise<SportTypeGroup[]>>();

function currentLangKey() {
  return normalizeLocale(i18n.resolvedLanguage || i18n.language || 'en');
}

export function invalidateSportTypeCatalogCache() {
  catalogCache.clear();
  catalogPromise.clear();
}

if (typeof i18n.on === 'function') {
  i18n.on('languageChanged', () => invalidateSportTypeCatalogCache());
}

function normalizeSport(raw: Record<string, unknown>): SportTypeItem {
  return {
    id: Number(raw.id ?? raw.Id),
    name: String(raw.name ?? raw.Name ?? ''),
    emoji: String(raw.emoji ?? raw.Emoji ?? raw.icon ?? '🏆'),
    color: raw.color != null ? String(raw.color) : undefined,
    venueName: raw.venueName != null ? String(raw.venueName) : undefined,
    groupNoun: raw.groupNoun != null ? String(raw.groupNoun) : undefined,
    studentNoun: raw.studentNoun != null ? String(raw.studentNoun) : undefined,
    coachNoun: raw.coachNoun != null ? String(raw.coachNoun) : undefined,
    lessonNoun: raw.lessonNoun != null ? String(raw.lessonNoun) : undefined,
    subscriptionNoun: raw.subscriptionNoun != null ? String(raw.subscriptionNoun) : undefined,
    venueExample: raw.venueExample != null ? String(raw.venueExample) : undefined,
    groupExample: raw.groupExample != null ? String(raw.groupExample) : undefined,
    hasDemoSchool: !!(raw.hasDemoSchool ?? raw.HasDemoSchool),
    launchPriority: Number(raw.launchPriority ?? raw.LaunchPriority ?? 1),
    typicalInventory: Array.isArray(raw.typicalInventory) ? raw.typicalInventory.map(String) : []
  };
}

function normalizeGroup(raw: Record<string, unknown>): SportTypeGroup {
  const sports = Array.isArray(raw.sports) ? raw.sports : [];
  return {
    groupId: Number(raw.groupId ?? raw.GroupId),
    groupName: String(raw.groupName ?? raw.GroupName ?? ''),
    groupEmoji: String(raw.groupEmoji ?? raw.GroupEmoji ?? '🏆'),
    color: raw.color != null ? String(raw.color) : undefined,
    sports: sports.map((s) => normalizeSport(s as Record<string, unknown>))
  };
}

function applyDemoOnlyFilter(groups: SportTypeGroup[], demoOnly: boolean): SportTypeGroup[] {
  if (!demoOnly) return groups;
  return groups
    .map((g) => ({
      ...g,
      sports: g.sports.filter((s) => s.hasDemoSchool)
    }))
    .filter((g) => g.sports.length > 0);
}

async function fetchFromApi(): Promise<SportTypeGroup[]> {
  const lang = normalizeLocale(i18n.resolvedLanguage || i18n.language || 'en');
  const headers = { 'Accept-Language': lang };
  try {
    const res = await api.get('/sport-types/catalog', { headers });
    return (Array.isArray(res.data) ? res.data : []).map((g) =>
      normalizeGroup(g as Record<string, unknown>)
    );
  } catch (catalogErr) {
    console.warn('Sport types /catalog failed, trying /sport-types', catalogErr);
    const res = await api.get('/sport-types', { headers });
    return (Array.isArray(res.data) ? res.data : []).map((g) =>
      normalizeGroup(g as Record<string, unknown>)
    );
  }
}

/** GET /api/sport-types[/catalog] — сгруппированный каталог (кэш по языку). */
export async function fetchSportTypeCatalog(
  demoOnly = false
): Promise<SportTypeGroup[]> {
  const langKey = currentLangKey();
  const cached = catalogCache.get(langKey);
  if (cached) return applyDemoOnlyFilter(cached, demoOnly);

  let pending = catalogPromise.get(langKey);
  if (!pending) {
    pending = (async () => {
      try {
        const groups = dedupeSportGroups(await fetchFromApi());
        catalogCache.set(langKey, groups);
        const total = groups.reduce((acc, g) => acc + g.sports.length, 0);
        console.log(`Sport types loaded (${langKey}):`, total);
        return groups;
      } catch (err) {
        console.warn('Sport types API unavailable, using local fallback:', err);
        const fallback = buildLocalSportTypeGroups(false);
        catalogCache.set(langKey, fallback);
        return fallback;
      } finally {
        catalogPromise.delete(langKey);
      }
    })();
    catalogPromise.set(langKey, pending);
  }

  const groups = await pending;
  return applyDemoOnlyFilter(groups, demoOnly);
}

export function dedupeSportItems(items: SportTypeItem[]): SportTypeItem[] {
  const seen = new Set<number>();
  return items.filter((s) => {
    if (!s.id || seen.has(s.id)) return false;
    seen.add(s.id);
    return true;
  });
}

export function dedupeSportGroups(groups: SportTypeGroup[]): SportTypeGroup[] {
  const seenGroups = new Set<number>();
  return groups
    .filter((g) => {
      if (seenGroups.has(g.groupId)) return false;
      seenGroups.add(g.groupId);
      return true;
    })
    .map((g) => ({
      ...g,
      sports: dedupeSportItems(g.sports),
    }));
}

export function flattenSportCatalog(groups: SportTypeGroup[]): SportTypeItem[] {
  return dedupeSportItems(groups.flatMap((g) => g.sports));
}

export function isSportSelectable(s: SportTypeItem): boolean {
  return s.launchPriority < 3;
}
